import { useEffect, useMemo, useRef, useState } from 'react';
import type { Geometry, VenueMapTopology } from '@gatecheckpro/contracts';

/**
 * The room, drawn.
 *
 * SVG rather than Canvas, because a seat is a thing a person needs to reach:
 * real elements can be focused, labelled and hit-tested by the browser, and a
 * canvas would mean reimplementing all three badly. The renderer is
 * first-party — no seat-map SDK — so the geometry the server owns is the only
 * geometry drawn (ADR 0036).
 *
 * **The projection never changes topology.** Culling and level-of-detail decide
 * what is painted, never what exists. A seat outside the viewport is still in
 * the document, still saved, and still counted; it simply has no element while
 * nobody can see it. That distinction is what lets a 100,000-seat room open at
 * all, and it is why the seat count on screen comes from the document rather
 * than from the number of circles drawn.
 *
 * The regime is chosen by how many chairs are actually in view, not by the zoom
 * level, because those are different questions. A three-hundred-seat theatre
 * fits on screen whole and should draw every chair; an arena at the same zoom
 * has a hundred thousand and must not. Keying on zoom would get both wrong.
 *
 * When too many chairs are in view, none are drawn and the room reads as
 * blocks — deliberately none rather than the first few thousand. A truncated
 * room is a lie: it looks like a map with seats missing, and the person cannot
 * tell which of the two they are looking at.
 */

/** More chairs than this in view and the room is drawn as blocks instead. */
const MAX_PAINTED_SEATS = 4000;
/** Rows are cheap, but not free. */
const MAX_PAINTED_ROWS = 2000;

interface Viewport {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function boundsOf(geometry: Geometry | null | undefined): Viewport | null {
  if (geometry === null || geometry === undefined) return null;
  switch (geometry.kind) {
    case 'POINT':
      return { x: geometry.x, y: geometry.y, width: 0, height: 0 };
    case 'RECT':
      return { x: geometry.x, y: geometry.y, width: geometry.width, height: geometry.height };
    case 'CIRCLE':
      return {
        x: geometry.x - geometry.radius,
        y: geometry.y - geometry.radius,
        width: geometry.radius * 2,
        height: geometry.radius * 2,
      };
    case 'POLYGON':
    case 'POLYLINE': {
      const xs = geometry.points.map((point) => point.x);
      const ys = geometry.points.map((point) => point.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
    }
  }
}

function intersects(bounds: Viewport | null, viewport: Viewport): boolean {
  if (bounds === null) return false;
  return (
    bounds.x <= viewport.x + viewport.width &&
    bounds.x + bounds.width >= viewport.x &&
    bounds.y <= viewport.y + viewport.height &&
    bounds.y + bounds.height >= viewport.y
  );
}

/**
 * How one chair or section is dressed by whatever is layered over the room.
 *
 * The canvas takes an appearance and applies it. It does not know what a seating
 * configuration is, what availability means, or where either came from — a
 * renderer that understood commerce would be one that could disagree with the
 * server about it.
 */
export interface OverlayAppearance {
  /** Appended to the element's class, so the palette stays in the stylesheet. */
  readonly modifier: string;
  /** Added to the accessible name, because colour is never the only signal. */
  readonly announce: string;
}

export interface VenueMapCanvasProps {
  readonly topology: VenueMapTopology;
  /** Keyed on the document's own seat key. One lookup per drawn chair. */
  readonly seatOverlay?: ReadonlyMap<string, OverlayAppearance> | undefined;
  /**
   * Keyed on section code, and used when chair detail is suppressed. Without it
   * a large configured room reads as an unconfigured one the moment level of
   * detail kicks in, which is the most misleading thing this surface could do.
   */
  readonly sectionOverlay?: ReadonlyMap<string, OverlayAppearance> | undefined;
  readonly selectedSeatKeys?: ReadonlySet<string> | undefined;
  /** Absent means read-only: an approved room is looked at, not edited. */
  readonly onSeatToggle?: ((seatKey: string) => void) | undefined;
  /**
   * The chairs actually being drawn, whenever that set changes.
   *
   * Culling already decides this and, until now, kept it: the renderer knew
   * which hundred of a hundred thousand chairs were on screen and nothing
   * outside could find out. Anything that wants live state for *what a person
   * is looking at* — rather than for the whole room — has to start here.
   */
  readonly onDrawnSeatsChange?: ((seatKeys: readonly string[]) => void) | undefined;
  /**
   * Which name a chair announces itself by.
   *
   * `code` is the authoring handle — "NORTH", "A" — and is what an organizer
   * works in. A customer has never seen those and should not start now, so the
   * attendee surface asks for the display names the room is labelled with.
   */
  readonly announceWith?: 'code' | 'displayName' | undefined;
  /** Test seam: fixes the viewport so a render is deterministic. */
  readonly initialScale?: number | undefined;
}

export function VenueMapCanvas({
  topology,
  seatOverlay,
  sectionOverlay,
  selectedSeatKeys,
  onSeatToggle,
  onDrawnSeatsChange,
  announceWith = 'code',
  initialScale = 1,
}: VenueMapCanvasProps) {
  const { canvas } = topology;

  /*
   * Names looked up once, not searched per chair. A linear scan of a hundred
   * sections for each of a thousand drawn chairs is the shape of thing the
   * level-of-detail work spent its time removing.
   */
  const placeNames = useMemo(() => {
    const sections = new Map<string, string>();
    const rows = new Map<string, string>();
    for (const section of topology.sections) {
      sections.set(section.code, section.displayName);
      for (const row of section.rows) rows.set(row.code, row.displayName);
    }
    return { sections, rows };
  }, [topology]);

  /**
   * Where a chair is, said the way this surface names places.
   *
   * "Seat 4, NORTH row A" for somebody configuring the room; "Seat 4, Balcony,
   * Row 12" for somebody buying a ticket in it. Same chair, and the drawing
   * still keys on the code either way — only the sentence changes.
   */
  const placeOf = (seat: VenueMapTopology['seats'][number]): string => {
    if (announceWith === 'code') {
      return `${seat.sectionCode}${
        seat.rowCode === null || seat.rowCode === undefined ? '' : ` row ${seat.rowCode}`
      }`;
    }
    return [
      placeNames.sections.get(seat.sectionCode) ?? seat.sectionCode,
      seat.rowCode === null || seat.rowCode === undefined
        ? undefined
        : placeNames.rows.get(seat.rowCode),
    ]
      .filter((part) => part !== undefined && part !== '')
      .join(', ');
  };
  const [scale, setScale] = useState(initialScale);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const dragFrom = useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);

  const viewport: Viewport = useMemo(
    () => ({
      x: origin.x,
      y: origin.y,
      width: canvas.width / scale,
      height: canvas.height / scale,
    }),
    [origin, scale, canvas.width, canvas.height],
  );

  /**
   * Culling counts past the ceiling before it stops, so the surface can say how
   * many chairs are in view rather than only that there are too many. Stopping
   * at the ceiling would leave "4,000+" as the only honest thing to print.
   */
  const inView = useMemo(() => {
    const visible = [];
    for (const seat of topology.seats) {
      if (intersects(boundsOf(seat.geometry), viewport)) visible.push(seat);
    }
    return visible;
  }, [topology.seats, viewport]);

  const showSeats = inView.length <= MAX_PAINTED_SEATS;
  const paintedSeats = showSeats ? inView : [];
  const showRows =
    topology.sections.reduce((total, section) => total + section.rows.length, 0) <=
    MAX_PAINTED_ROWS;

  /**
   * Reported after the render that produced it, and only when the set actually
   * changes. A join key rather than the array itself, because panning by a
   * pixel redraws the same chairs and re-fetching their state for that would be
   * worse than not offering this at all.
   */
  const drawnKey = paintedSeats.map((seat) => seat.seatKey).join('\u0000');
  // The callback is held in a ref and deliberately not a dependency. Callers
  // pass an inline function — that is the ordinary way to use this — and a new
  // identity every render would fire on every render, which is exactly the
  // re-fetch this notification exists to avoid.
  const notify = useRef(onDrawnSeatsChange);
  useEffect(() => {
    notify.current = onDrawnSeatsChange;
  });
  useEffect(() => {
    notify.current?.(drawnKey === '' ? [] : drawnKey.split('\u0000'));
  }, [drawnKey]);

  const viewBox = `${String(Math.round(viewport.x))} ${String(Math.round(viewport.y))} ${String(
    Math.round(viewport.width),
  )} ${String(Math.round(viewport.height))}`;

  return (
    <div className="gc-stack gc-stack--tight">
      <div className="gc-row gc-row--between">
        <div className="gc-row" role="group" aria-label="Zoom">
          <button
            type="button"
            className="gc-button"
            onClick={() =>
              setScale((current) => Math.max(0.05, Number((current / 1.5).toFixed(3))))
            }
          >
            Zoom out
          </button>
          <button
            type="button"
            className="gc-button"
            onClick={() => setScale((current) => Math.min(8, Number((current * 1.5).toFixed(3))))}
          >
            Zoom in
          </button>
          <button
            type="button"
            className="gc-button"
            onClick={() => {
              setScale(1);
              setOrigin({ x: 0, y: 0 });
            }}
          >
            Fit the room
          </button>
          <span className="gc-muted gc-mono" data-testid="venue-map-zoom">
            {Math.round(scale * 100)}%
          </span>
        </div>
        <span className="gc-muted gc-mono" data-testid="venue-map-painted-seats">
          {paintedSeats.length.toLocaleString()} of {topology.seats.length.toLocaleString()} seats
          drawn
        </span>
      </div>

      <svg
        role="img"
        aria-label={`Venue map, ${String(topology.seats.length)} seats`}
        data-testid="venue-map-canvas"
        viewBox={viewBox}
        className="gc-venue-map"
        onPointerDown={(pointer) => {
          dragFrom.current = {
            x: pointer.clientX,
            y: pointer.clientY,
            originX: origin.x,
            originY: origin.y,
          };
        }}
        onPointerMove={(pointer) => {
          const from = dragFrom.current;
          if (from === null) return;
          setOrigin({
            x: Math.round(from.originX - (pointer.clientX - from.x) / scale),
            y: Math.round(from.originY - (pointer.clientY - from.y) / scale),
          });
        }}
        onPointerUp={() => {
          dragFrom.current = null;
        }}
        onPointerLeave={() => {
          dragFrom.current = null;
        }}
      >
        {topology.sections.map((section) => {
          const bounds = boundsOf(section.geometry);
          if (bounds === null) return null;
          return (
            <g key={section.code}>
              <rect
                x={bounds.x}
                y={bounds.y}
                width={bounds.width}
                height={bounds.height}
                className={
                  sectionOverlay?.has(section.code) === true
                    ? `gc-venue-map__section gc-venue-map__section--${sectionOverlay.get(section.code)?.modifier ?? ''}`
                    : 'gc-venue-map__section'
                }
                data-section-code={section.code}
                data-section-overlay={sectionOverlay?.get(section.code)?.modifier}
              >
                <title>
                  {section.displayName}
                  {sectionOverlay?.has(section.code) === true
                    ? ` — ${sectionOverlay.get(section.code)?.announce ?? ''}`
                    : ''}
                </title>
              </rect>
              <text x={bounds.x + 8} y={bounds.y + 24} className="gc-venue-map__label">
                {section.displayName}
              </text>
              {showRows &&
                section.tables.map((table) => {
                  const table_bounds = boundsOf(table.geometry);
                  if (table_bounds === null) return null;
                  return (
                    <circle
                      key={table.code}
                      cx={table_bounds.x + table_bounds.width / 2}
                      cy={table_bounds.y + table_bounds.height / 2}
                      r={table_bounds.width / 2}
                      className="gc-venue-map__table"
                    />
                  );
                })}
              {showRows &&
                section.rows.map((row) => {
                  const geometry = row.geometry;
                  if (geometry === null || geometry === undefined || geometry.kind !== 'POLYLINE') {
                    return null;
                  }
                  const [start, end] = geometry.points;
                  if (start === undefined || end === undefined) return null;
                  return (
                    <line
                      key={row.code}
                      x1={start.x}
                      y1={start.y}
                      x2={end.x}
                      y2={end.y}
                      className="gc-venue-map__row"
                    />
                  );
                })}
            </g>
          );
        })}

        {paintedSeats.map((seat) => {
          const geometry = seat.geometry;
          if (geometry === null || geometry === undefined || geometry.kind !== 'POINT') return null;
          const selected = selectedSeatKeys?.has(seat.seatKey) === true;
          // One map lookup per drawn chair. Scanning the configuration instead
          // would be 144 million comparisons on a 12,000-seat room, and a hung
          // tab at the contract's ceiling.
          const dressed = seatOverlay?.get(seat.seatKey);
          return (
            <circle
              key={seat.seatKey}
              cx={geometry.x}
              cy={geometry.y}
              r={geometry.radius ?? 12}
              className={[
                'gc-venue-map__seat',
                dressed === undefined ? '' : `gc-venue-map__seat--${dressed.modifier}`,
                selected ? 'gc-venue-map__seat--on' : '',
              ]
                .filter((name) => name !== '')
                .join(' ')}
              data-seat-key={seat.seatKey}
              data-overlay={dressed?.modifier}
              tabIndex={onSeatToggle === undefined ? -1 : 0}
              role={onSeatToggle === undefined ? undefined : 'button'}
              aria-pressed={onSeatToggle === undefined ? undefined : selected}
              aria-label={`Seat ${seat.seatLabel}, ${placeOf(seat)}${
                dressed === undefined ? '' : `, ${dressed.announce}`
              }`}
              onClick={() => onSeatToggle?.(seat.seatKey)}
              onKeyDown={(key) => {
                if (key.key === 'Enter' || key.key === ' ') {
                  key.preventDefault();
                  onSeatToggle?.(seat.seatKey);
                }
              }}
            />
          );
        })}
      </svg>

      {!showSeats && (
        <p className="gc-muted" data-testid="venue-map-lod-notice">
          {inView.length.toLocaleString()} seats are in view, which is more than can be drawn
          legibly, so the room is shown as blocks. Every seat is still there and still saved — zoom
          in to work with them.
        </p>
      )}
    </div>
  );
}
